import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { Phone, Mail, MapPin, Linkedin, Twitter } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();

  // Get language-specific legal URLs
  const getPrivacyUrl = () => {
    if (language === 'ru') return '/ru/privacy-policy';
    if (language === 'ar') return '/ar/privacy-policy';
    return '/privacy-policy';
  };

  const getCookieUrl = () => {
    if (language === 'ru') return '/ru/cookie-notice';
    if (language === 'ar') return '/ar/cookie-notice';
    return '/cookie-notice';
  };

  const navigation = {
    product: [
      { name: 'VLAGEN OS', href: '/vlagen-os' },
      { name: t.footer?.methodology || 'Architecture', href: '/architecture' },
      { name: t.footer?.industries || 'Solutions', href: '/solutions' },
      { name: t.footer?.cases || 'Case Studies', href: '/cases' },
    ],
    company: [
      { name: t.footer?.about || 'About', href: '/company' },
      { name: t.footer?.partners || 'Partners', href: '/partners' },
      { name: t.footer?.blog || 'Blog', href: '/blog' },
      { name: t.footer?.contact || 'Contact', href: '/contact' },
    ],
    legal: [
      { name: t.footer?.privacy || 'Privacy Policy', href: getPrivacyUrl() },
      { name: t.footer?.cookies || 'Cookie Notice', href: getCookieUrl() },
    ],
  };

  return (
    <footer className="bg-[#0a0a0b] border-t border-[#1f1f23]" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#2ec4aa] rounded-xl flex items-center justify-center">
                <span className="text-black font-bold text-lg">V</span>
              </div>
              <span className="text-white font-semibold text-xl tracking-tight">VLAGEN</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {t.footer?.brand || 'Operational infrastructure for complex business processes.'}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-lg bg-[#1f1f23] flex items-center justify-center text-gray-400 hover:text-[#2ec4aa] hover:bg-[#2ec4aa]/10 transition-all"
              >
                <Linkedin size={18} aria-hidden="true" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="w-10 h-10 rounded-lg bg-[#1f1f23] flex items-center justify-center text-gray-400 hover:text-[#2ec4aa] hover:bg-[#2ec4aa]/10 transition-all"
              >
                <Twitter size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">
              {t.footer?.product || 'Product'}
            </h4>
            <ul className="space-y-4">
              {navigation.product.map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.href} 
                    className="text-gray-400 hover:text-[#2ec4aa] transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">
              {t.footer?.company || 'Company'}
            </h4>
            <ul className="space-y-4">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.href} 
                    className="text-gray-400 hover:text-[#2ec4aa] transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">
              {t.footer?.legal || 'Legal'}
            </h4>
            <ul className="space-y-4">
              {navigation.legal.map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.href} 
                    className="text-gray-400 hover:text-[#2ec4aa] transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-6">
              {t.footer?.contactTitle || 'Contact'}
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+971582965700" 
                  className="flex items-center gap-3 text-gray-400 hover:text-[#2ec4aa] transition-colors text-sm"
                >
                  <Phone size={16} />
                  +971 58 296 5700
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@vlagengroup.com" 
                  className="flex items-center gap-3 text-gray-400 hover:text-[#2ec4aa] transition-colors text-sm"
                >
                  <Mail size={16} />
                  info@vlagengroup.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>{t.footer?.address || 'Dubai, UAE'}</span>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#1f1f23]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} VLAGEN. {t.footer?.rights || 'All rights reserved.'}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
